import toNumber from 'lodash/toNumber'
import isNaN from 'lodash/isNaN'
import type { FunctionComponent } from 'react'
import React from 'react'
import { NumberInput } from '../Number/NumberInput'
import { HorizontalStack, ShowIf } from '@ztechapps/webui.util'
import { generateId } from '@ztechapps/common.utils'
import styles from './RangeSliderInput.module.scss'
import merge from 'lodash/merge'
import { Stack } from 'react-bootstrap'

export type RangeSliderInputComponents = {
  minNumberInput: FunctionComponent<any>
  maxNumberInput: FunctionComponent<any>
}

export const DefaultRangeSliderInputComponents = {
  minNumberInput: (props) => <NumberInput name={'rangeMinInput'} {...props} />,
  maxNumberInput: (props) => <NumberInput name={'rangeMaxInput'} {...props} />,
}

export type RangeSliderInputProps = {
  max?: number
  step?: number
  value?: { start: number; end: number }
  onChange: any
  disabled?: boolean
  components?: RangeSliderInputComponents
  inputPosition?: 'left-right' | 'top' | 'bottom'
  showInput?: boolean
}
export const RangeSliderInput = ({
  max = 100,
  step = 1,
  value = { start: 8, end: 60 },
  onChange,
  disabled,
  components,
  inputPosition = 'left-right',
  showInput = true,
}: RangeSliderInputProps) => {
  components = merge(components, DefaultRangeSliderInputComponents)

  const [rangeMaxValue, setRangeMaxValue] = React.useState(value?.end)
  const [inverseRight, setInverseRight] = React.useState((value?.end / max) * 100)

  const [rangeMinValue, setRangeMinValue] = React.useState(value?.start)
  const [inverseLeft, setInverseLeft] = React.useState((value?.start / max) * 100)

  React.useEffect(() => {
    setRangeMinValue(value?.start)
    setInverseLeft((value?.start / max) * 100)
  }, [value?.start, max])

  React.useEffect(() => {
    setRangeMaxValue(value?.end)
    setInverseRight((value?.end / max) * 100)
  }, [value?.end, max])

  const minRangeRef = React.useRef(null)
  const maxRangeRef = React.useRef(null)

  const changeMinValue = React.useCallback(
    (newValue) => {
      //the maximum value should be 100
      const inverseLeftValue = Math.min((newValue / max) * 100, 100)

      setInverseLeft(inverseLeftValue)

      setRangeMinValue(Math.min(newValue, max - step))
    },
    [max, step, setInverseLeft, setRangeMinValue]
  )

  const onChangeMin = React.useCallback(
    (e) => {
      const newValue = Math.min(e?.target?.value, rangeMaxValue)
      if (newValue >= rangeMaxValue) return

      changeMinValue(newValue)
    },
    [rangeMaxValue, changeMinValue]
  )

  const changeMaxValue = React.useCallback(
    (newValue) => {
      //the maximum value should be 100
      const inverseRightValue = Math.min((newValue / max) * 100, 100)

      setInverseRight(inverseRightValue)

      setRangeMaxValue(Math.min(newValue, max))
    },
    [max, setInverseRight, setRangeMaxValue]
  )

  const onChangeMax = React.useCallback(
    (e) => {
      const newValue = Math.max(e?.target?.value, rangeMinValue)
      if (newValue <= rangeMinValue) return

      changeMaxValue(newValue)
    },
    [rangeMinValue, changeMaxValue]
  )

  const rangeId = React.useMemo(() => {
    return `${generateId()}`
  }, [])
  const minNumberInput = (
    <components.minNumberInput
      {...{
        disabled,
        value: rangeMinValue,
        step,
        name: 'rangeMinInput',
        onChange: (e) => {
          const newValue = isNaN(e?.target?.value) ? rangeMaxValue - step : e?.target?.value
          // if (newValue > maximumValue) newValue = maximumValue - step
          changeMinValue(newValue)
        },
        onBlur: (e) => {
          let newStartValue = toNumber(e?.target?.value),
            newEndValue = rangeMaxValue

          if (newStartValue > rangeMaxValue) {
            newEndValue = newStartValue + step
            changeMaxValue(newEndValue)
          }

          if (newStartValue < 0) newStartValue = 0
          changeMinValue(newStartValue)

          onChange?.({ start: newStartValue, end: newEndValue })
        },
        onFocus: (e) => {
          e?.target?.select()
        },
        inputRef: (ref) => {
          if (ref) ref.id = `${rangeId}-1`
        },
        onKeyDown: (event) => {
          if (event.keyCode === 13) {
            event.preventDefault()
            const nextNode = document.getElementById(`${rangeId}-2`)
            if (!nextNode) return

            nextNode?.focus()
            //nextNode.select && nextNode.select()
          }
        },
      }}
    />
  )
  const maxNumberInput = (
    <components.maxNumberInput
      {...{
        disabled,
        value: rangeMaxValue,
        step,
        name: 'rangeMaxInput',
        onChange: (e) => {
          const newValue = isNaN(e?.target?.value) ? rangeMinValue + step : e?.target?.value
          // if (newValue > maximumValue) newValue = maximumValue

          changeMaxValue(newValue)
        },
        onBlur: (e) => {
          let newStartValue = rangeMinValue
          const newEndValue = toNumber(e?.target?.value)

          if (newEndValue < rangeMinValue) {
            newStartValue = newEndValue - step
            changeMinValue(newStartValue)
          }

          changeMaxValue(newEndValue)
          onChange?.({ start: newStartValue, end: newEndValue })
        },
        onFocus: (e) => {
          e?.target?.select()
        },
        inputRef: (ref) => {
          if (ref) ref.id = `${rangeId}-2`
        },
        onKeyDown: (event) => {
          if (event.keyCode === 13) {
            event.preventDefault()
            const nextNode = document.getElementById(`${rangeId}-1`)
            if (!nextNode) return

            nextNode.focus()
            //nextNode.select && nextNode.select()
          }
        },
      }}
    />
  )

  return (
    <Stack gap={2}>
      <ShowIf condition={inputPosition === 'top' && showInput}>
        <HorizontalStack gap={3}>
          <div>{minNumberInput}</div>
          <div>{maxNumberInput}</div>
        </HorizontalStack>
      </ShowIf>

      <HorizontalStack gap={0} className={styles.sliderContainer}>
        <ShowIf condition={inputPosition === 'left-right' && showInput}>{minNumberInput}</ShowIf>
        <div className={styles.slider}>
          <div>
            <div className={styles.inverseLeft} style={{ width: `${inverseLeft}%` }} />
            <div className={styles.inverseRight} style={{ width: `${100 - inverseRight}%` }} />
            <div
              className={`${styles.rangeValue} ${disabled ? styles.disabled : ''}`}
              style={{
                left: `${(rangeMinValue / max) * 100}%`,
                right: `${100 - (rangeMaxValue / max) * 100}%`,
              }}
            />
            <span
              className={`${styles.thumb} ${disabled ? styles.disabled : ''}`}
              style={{ left: `${inverseLeft}%` }}
            />
            <span
              className={`${styles.thumb} ${disabled ? styles.disabled : ''}`}
              style={{ left: `${inverseRight}%` }}
            />
          </div>

          <input
            type="range"
            tabIndex={0}
            value={rangeMinValue}
            max={max}
            step={step}
            onChange={onChangeMin}
            ref={minRangeRef}
            onMouseUp={() => {
              onChange?.({ start: rangeMinValue, end: rangeMaxValue })
            }}
            onTouchEnd={() => {
              onChange?.({ start: rangeMinValue, end: rangeMaxValue })
            }}
            disabled={disabled}
          />

          <input
            type="range"
            tabIndex={1}
            value={rangeMaxValue}
            max={max}
            step={step}
            onChange={onChangeMax}
            ref={maxRangeRef}
            onMouseUp={() => {
              onChange?.({ start: rangeMinValue, end: rangeMaxValue })
            }}
            onTouchEnd={() => {
              onChange?.({ start: rangeMinValue, end: rangeMaxValue })
            }}
            disabled={disabled}
          />
        </div>
        <ShowIf condition={inputPosition === 'left-right' && showInput}>{maxNumberInput}</ShowIf>
      </HorizontalStack>

      <ShowIf condition={inputPosition === 'bottom' && showInput}>
        <HorizontalStack gap={3}>
          <div>{minNumberInput}</div>
          <div>{maxNumberInput}</div>
        </HorizontalStack>
      </ShowIf>
    </Stack>
  )
}
